// **React Imports
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// **Component Imports
import ControlledTextfield from '../../../../../components/controlled-textfield'
import CustomDateTimePicker from '../../../../../components/date-time-picker'
import CustomDialog from '../../../../../components/dialog'
import Icon from '../../../../../components/icon'
import UserAvatar from '../../../../../components/user-avatar'
import VirtualizedAutocomplete from '../../../../../components/virtualized-autocomplete'

// **MUI Imports
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup
} from '@mui/material'

// **API Imports
import {
  enableMoveToNextStage,
  getFeatureQuota
} from '../../../../../axios/api/contest'
import { getNonLearnerUsersOfWorkspace } from '../../../../../axios/api/workspace'

// **Third Party Imports
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import * as yup from 'yup'

// **Utils Imports
import QuotaTooltip from '../../../../../components/quota-tooltip'
import { CHALLENGE_TYPES, SORT_TYPE } from '../../../../../utils/constant'
import {
  commonClientSort,
  hexToRGBA,
  showErrorToast
} from '../../../../../utils/utils'
import {
  scoreValidation,
  validateRequiredString
} from '../../../../../utils/validations'

// constansts
const defaultValues = {
  score: 0,
  feedback: 'Candidate not selected.',
  interviewerIds: []
}

const MoveToNextStageModal = ({
  open,
  nextStageId,
  attemptIds,
  rejectIds,
  handleClose,
  startTime = null,
  hideRejectOthers = false,
  stageName,
  stageType,
  handleEnableClick,
  refreshHandler = () => {}
}) => {
  // redux state
  const workspaceId = useSelector(
    (state) => state.workspace.selectedWorkspace?.id
  )

  // State
  const [loading, setLoading] = useState({
    submit: false,
    quota: false,
    users: false
  })
  const [quotaDetails, setQuotaDetails] = useState(null)
  const [scheduleStage, setScheduleStage] = useState(false)
  const [rejectionInfo, setRejectionInfo] = useState(null)
  const [userOptions, setUserOptions] = useState([])

  const [data, setData] = useState({
    startTime: '',
    rejectIds: [],
    attemptIds: [],
    rejectOthers: false
  })

  // variables
  const isInterviewStage = useMemo(() => {
    return stageType === CHALLENGE_TYPES.INTERVIEW
  }, [stageType])

  // schema
  const schema = useMemo(() => {
    return yup.object().shape({
      score: scoreValidation(),
      feedback: validateRequiredString('Feedback'),
      interviewerIds: isInterviewStage
        ? yup
            .array()
            .of(yup.string())
            .min(1, 'Please select at least one interviewer')
        : yup.array().notRequired()
    })
  }, [isInterviewStage])

  // form hook
  const {
    control,
    handleSubmit,
    clearErrors,
    setValue,
    trigger,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  // other variables
  const selectedInterviewer = watch('interviewerIds')

  const dropdownValue = useMemo(() => {
    const data = userOptions?.filter((opt) => {
      return selectedInterviewer?.includes(opt?.id)
    })
    return data
  }, [userOptions, selectedInterviewer])

  // Handlers
  const fetchQuotaDetails = useCallback(
    async (stageId, estimatedCandidates) => {
      setLoading((prev) => ({ ...prev, quota: true }))
      const response = await getFeatureQuota(stageId, estimatedCandidates)
      setLoading((prev) => ({ ...prev, quota: false }))

      if (response?.isError) {
        showErrorToast(response)
      } else {
        setQuotaDetails(response)
      }
    },
    []
  )
  const fetchUserOptions = useCallback(async (workspaceId) => {
    setLoading((prev) => ({ ...prev, users: true }))
    const response = await getNonLearnerUsersOfWorkspace(workspaceId, false)
    setLoading((prev) => ({ ...prev, users: false }))

    if (response?.isError) {
      showErrorToast(response)
      setUserOptions([])
    } else {
      const sortedUser = [...(response || [])]?.sort((a, b) =>
        commonClientSort(a?.name, b?.name, 'asc', SORT_TYPE?.STRING)
      )
      setUserOptions(sortedUser)
    }
  }, [])

  const handleDateChange = useCallback((value) => {
    setData((prev) => ({
      ...prev,
      startTime: value ? new Date(value).toISOString() : value
    }))
  }, [])

  const handleRejectOthersChange = useCallback(
    (checked) => {
      if (checked) {
        setRejectionInfo(`${rejectIds?.length} candidate(s) will get rejected`)

        setData((prev) => ({
          ...prev,
          rejectOthers: checked,
          rejectIds: rejectIds
        }))
      } else {
        setData((prev) => ({ ...prev, rejectOthers: checked, rejectIds: [] }))
      }
    },
    [rejectIds]
  )

  const handleMoveToNextStageClick = useCallback(
    async (details) => {
      const payload = { ...data }
      if (data.rejectOthers) {
        payload.score = details?.score
        payload.feedback = details?.feedback
        payload.attemptIds = attemptIds
      }
      delete payload.rejectOthers
      if (!scheduleStage) {
        payload.startTime = null
      }
      if (isInterviewStage) {
        payload.interviewerIds = details?.interviewerIds || []
      }

      if (typeof handleEnableClick === 'function') {
        handleEnableClick(payload)
        return
      }

      setLoading((prev) => ({ ...prev, submit: true }))
      const response = await enableMoveToNextStage(payload)
      setLoading((prev) => ({ ...prev, submit: false }))

      if (response?.isError) {
        showErrorToast(response)
      } else {
        toast.success('Candidate moved to next stage successfully')
        handleClose()
        refreshHandler()
      }
    },
    [
      isInterviewStage,
      data,
      handleClose,
      refreshHandler,
      scheduleStage,
      attemptIds,
      handleEnableClick
    ]
  )

  const onSubmit = useCallback(
    (data) => {
      handleMoveToNextStageClick(data)
    },
    [handleMoveToNextStageClick]
  )

  // useEffects
  useEffect(() => {
    if (!open) {
      setData({ attemptIds: [], startTime: '' })
      setScheduleStage(false)
      setQuotaDetails(null)
      setRejectionInfo(null)
      clearErrors()
      setLoading({
        submit: false,
        quota: false,
        users: false
      })
      reset()
      setUserOptions([])
    } else {
      setScheduleStage(!!startTime)

      setData((prev) => ({ ...prev, attemptIds, startTime }))
    }
  }, [attemptIds, open, startTime, clearErrors, reset])

  // effect to fetch quota on stage Id
  useEffect(() => {
    if (nextStageId && open && data?.attemptIds?.length > 0) {
      fetchQuotaDetails(nextStageId, data?.attemptIds?.length)
    }
  }, [open, nextStageId, fetchQuotaDetails, data?.attemptIds?.length])

  useEffect(() => {
    if (workspaceId && isInterviewStage && open) {
      fetchUserOptions(workspaceId)
    }
  }, [isInterviewStage, workspaceId, fetchUserOptions, open])

  return (
    <CustomDialog
      open={open}
      outsideClikClose={false}
      handleClose={handleClose}
      title={`Move to ${stageName || 'Stage'} `}
      renderHeader={() => (
        <QuotaTooltip data={quotaDetails} loading={loading?.quota} />
      )}
      dialogPaperStyle={{
        maxWidth: '550px',
        width: '40%',
        '& .MuiDialogContent-root': {
          padding: '0px !important'
        }
      }}
    >
      <Grid container>
        <Grid item xs={12} container padding={'10px 20px 0px 20px'} spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <RadioGroup
                onChange={(e) => {
                  const value = e.target.value === 'true'
                  setScheduleStage(value)
                }}
                row
                sx={{ marginLeft: '6px' }}
                value={scheduleStage}
              >
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="Enable now"
                />
                <FormControlLabel
                  sx={{ flex: 'unset' }}
                  value={true}
                  control={<Radio />}
                  label="Schedule for later"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {scheduleStage && (
            <Grid item xs={12}>
              <CustomDateTimePicker
                label={`Stage Start Time`}
                value={data?.startTime}
                onChange={handleDateChange}
                usedInHookForm={false}
                timeIntervals={5}
              />
            </Grid>
          )}

          {isInterviewStage && (
            <Grid item xs={12} mt={2}>
              <VirtualizedAutocomplete
                label={'Assign Interviewers'}
                options={userOptions}
                name="interviewerIds"
                control={control}
                needVirtualization={false}
                helperText={
                  errors?.interviewerIds?.message ||
                  'Please select at least one interviewer'
                }
                error={errors?.interviewerIds || false}
                autoCompleteProps={{
                  multiple: true,
                  options: userOptions,
                  disableCloseOnSelect: true,
                  disabled: loading?.users,
                  value: dropdownValue,
                  onChange: (_, newValue) => {
                    const value = newValue.map((item) => item.id)
                    setValue('interviewerIds', value)
                    trigger('interviewerIds')
                  },
                  getOptionLabel: (option) => option?.email || '',
                  renderOption: (props, option) => {
                    // variables
                    const { key, ...optionProps } = props

                    return (
                      <li key={key} {...optionProps} value={option}>
                        <UserAvatar
                          data={{
                            name: option?.name,
                            email: option?.email
                          }}
                        />
                      </li>
                    )
                  }
                }}
              />
            </Grid>
          )}

          {!hideRejectOthers && rejectIds?.length > 0 && (
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={data?.rejectOthers || false}
                    onChange={({ target: { checked } }) =>
                      handleRejectOthersChange(checked)
                    }
                  />
                }
                label={'Reject Other Candidates'}
              />
            </Grid>
          )}

          {rejectionInfo && (
            <Grid item xs={12}>
              <Button
                color="warning"
                sx={{
                  backgroundColor: hexToRGBA('#FFAB5A', 0.1),
                  cursor: 'default',
                  pointerEvents: 'none'
                }}
                startIcon={
                  <Icon
                    icon="material-symbols:info-outline-rounded"
                    style={{
                      color: hexToRGBA('var(--warning-main)', 0.2),
                      cursor: 'default'
                    }}
                  />
                }
              >
                {rejectionInfo}
              </Button>
            </Grid>
          )}

          {data?.rejectOthers && (
            <Grid item xs={12}>
              <Box>
                <Box mb={4}>
                  <ControlledTextfield
                    fullWidth
                    name="score"
                    numericValue
                    label="Score"
                    control={control}
                    error={Boolean(errors.score)}
                    helperText={errors.score?.message}
                    placeholder="Enter a score between 0 and 10"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">/10</InputAdornment>
                      )
                    }}
                  />
                </Box>
                <Box mb={4}>
                  <ControlledTextfield
                    multiline
                    fullWidth
                    minRows={4}
                    name="feedback"
                    control={control}
                    label="Feedback"
                    error={Boolean(errors.feedback)}
                    helperText={errors.feedback?.message}
                  />
                </Box>
              </Box>
            </Grid>
          )}

          {/* {loading?.quota ? (
            <Grid xs={12}>
              <Skeleton height={40} width={'100%'} animation="wave" />
            </Grid>
          ) : (
            <Grid item xs={12} mb={2}>
              <QuotaWidget data={quotaDetails} />
            </Grid>
          )} */}
        </Grid>
        <Grid item xs={12} padding={'20px'}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '16px'
            }}
          >
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              disabled={(scheduleStage && !data.startTime) || loading?.submit}
            >
              {loading?.enable || loading?.submit ? (
                <CircularProgress size={22} />
              ) : scheduleStage ? (
                'Schedule'
              ) : (
                'Enable'
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </CustomDialog>
  )
}

export default memo(MoveToNextStageModal)
